import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

import { interpolateString } from 'support/helpers/strings/interpolateString';
import { TaskBaseDTO, TaskDTO } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { SlideOver, SlideOverSubtitleSkeleton, SlideOverTitleSkeleton } from 'components/Display/SlideOver/SlideOver';
import { Button } from 'components/Form/Button/Button';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';
import { TASK_FORM_ID, TaskForm } from 'components/TaskForm/TaskForm';
import { Translate } from 'components/Translate/Translate';

type TaskSlideOverProps = {
  open: boolean;
  onClose: () => void;
  isLoading?: boolean;
  partnerName?: string;
  partnerLogoUrl?: string | null;
  task?: TaskBaseDTO | TaskDTO;
  variables: Record<string, string>;
  mode?: DisplayModes;
  isSubmitting?: boolean;
  relationshipId?: string;
  onSubmit?: (data: Record<string, unknown>) => void;
  topBanner?: ReactNode;
  extraContent?: ReactNode;
};

export const TaskSlideOver = ({
  open,
  onClose,
  isLoading = false,
  partnerName,
  partnerLogoUrl,
  task,
  variables,
  mode = DisplayModes.create,
  isSubmitting = false,
  relationshipId,
  onSubmit,
  topBanner,
  extraContent,
}: TaskSlideOverProps) => {
  const { t } = useTranslation();

  const isProcurosOwned = task?.ownership === 'PROCUROS';
  const showFooter = onSubmit && mode !== DisplayModes.view;

  return (
    <SlideOver
      size="large"
      open={open}
      setOpen={() => onClose()}
      title={isLoading ? <SlideOverTitleSkeleton /> : partnerName}
      subtitle={isLoading ? <SlideOverSubtitleSkeleton /> : interpolateString(task?.title, variables)}
      headerImage={<PartnerLogo imageUrl={partnerLogoUrl} size="large" isLoading={isLoading} />}
      topBanner={topBanner}
      footer={
        showFooter ? (
          <>
            <Button variant="secondary" onClick={onClose} analyticsId="task_slide_over_cancel">
              {t('common:cancel')}
            </Button>
            <Button
              type="submit"
              form={TASK_FORM_ID}
              variant="primary"
              loading={isSubmitting}
              LeftIcon={PaperAirplaneIcon}
              analyticsId="task_slide_over_submit"
            >
              {t('connections:relationshipDetails.task.submit')}
            </Button>
          </>
        ) : undefined
      }
    >
      {!isLoading && task && isProcurosOwned && (
        <div className="flex flex-col gap-6">
          {task.description && (
            <p className="text-sm text-gray-700">
              <Translate i18nKey={interpolateString(task.description, variables)} />
            </p>
          )}
          <div className="border-t border-gray-200" />
          <p className="text-sm leading-5 text-gray-500">
            {t('connections:relationshipDetails.task.procurosOwned.support')}
          </p>
        </div>
      )}
      {!isLoading && task && !isProcurosOwned && (
        <div className="flex flex-col gap-6">
          {extraContent}
          {task.description && (
            <p className="text-sm text-gray-700">
              <Translate i18nKey={interpolateString(task.description, variables)} />
            </p>
          )}
          <TaskForm
            key={task.id}
            task={task}
            mode={mode}
            isSubmitting={isSubmitting}
            variables={variables}
            relationshipId={relationshipId}
            onSubmit={onSubmit}
          />
        </div>
      )}
    </SlideOver>
  );
};
